import { useMemo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, cn, HelperText, Label, TextInput } from '@c2fo/liquidity';
import { PercentIcon } from '@c2fo/liquidity/icons';
import NameYourRateSlider from '@/enterprise/components/OfferBuilder/RateSelect/NameYourRate/NameYourRateSlider';
import RadioInput from '@/enterprise/components/RadioInput';
import usePricingSuggestion from '@/enterprise/data/usePricingSuggestion';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useOfferDiscountAPRDisplay } from '@/enterprise/features/takerMarketTable/utils/useOfferDiscountAPRDisplay';
import useDataDog from '@/reporting/DataDog';
import ErrorBoundary from '@/shared/components/ErrorBoundary';
import { useLocaleFormat } from '@/utils/useLocaleFormat';
import { PricingOption, SelectedStrategy, buildOptionsFromPricingData, parseRateInput } from './utils';

interface PricingStrategyOptionsProps {
  selectedStrategy: SelectedStrategy | null;
  onSelectStrategy: (strategy: SelectedStrategy) => void;
  rateTypeLabel: string;
  selectedRateType: 'APR' | 'DISC';
  errorMessage?: string | null;
  hasNoSelectionError?: boolean;
  onClearError?: () => void;
  takerMarket: TakerMarket;
  frequency: 'oneTime' | 'recurring';
  open?: boolean;
}

interface SuggestedRateOptionProps {
  option: PricingOption;
  selected: boolean;
  onSelect: () => void;
  rateTypeLabel: string;
  radioGroupName: string;
  hasNoSelectionError?: boolean;
}

interface NameYourRateOptionProps {
  takerMarket: TakerMarket;
  selected: boolean;
  onSelect: (rate: number, rateType: 'APR' | 'DISC') => void;
  radioGroupName: string;
  selectedRateType: 'APR' | 'DISC';
  open?: boolean;
  rateTypeLabel: string;
  trendingRateOption: PricingOption | null;
  errorMessage?: string | null;
  hasNoSelectionError?: boolean;
  onClearError?: () => void;
}

const SuggestedRateOption = ({
  option,
  selected,
  onSelect,
  rateTypeLabel,
  radioGroupName,
  hasNoSelectionError = false,
}: SuggestedRateOptionProps) => {
  const { t } = useTranslation();
  const { offerDiscountAPRDisplay } = useOfferDiscountAPRDisplay();

  const pricingTypeLabelMap = {
    expressAccept: t('core.expressAccept'),
    trendingRate: t('core.trending'),
  };

  return (
    <RadioInput
      id={`suggested-rate-${option.type}`}
      className={cn('border', { 'border-red-600': hasNoSelectionError })}
      checked={selected}
      color="blue"
      name={radioGroupName}
      endSlot={
        <div>
          {option.type === 'expressAccept' ? (
            <Chip label={t('core.instantSuccess')} size="sm" type="primary" variant="outlined" />
          ) : (
            <Chip label={t('core.aboveAverageSuccess')} size="sm" type="info" variant="outlined" />
          )}
        </div>
      }
      label={
        <div>
          <div className="font-bold uppercase">{offerDiscountAPRDisplay(option.rate, rateTypeLabel)}</div>
          <div className="text-sm font-normal text-text-secondary">{pricingTypeLabelMap[option.type]}</div>
        </div>
      }
      onChange={onSelect}
      size="large"
      value={option.type}
      variant="light"
    />
  );
};

const NameYourRateOption = ({
  takerMarket,
  selected,
  onSelect,
  radioGroupName,
  selectedRateType,
  rateTypeLabel,
  open,
  trendingRateOption,
  errorMessage,
  hasNoSelectionError = false,
  onClearError,
}: NameYourRateOptionProps) => {
  const { t } = useTranslation();
  const { asNumber } = useLocaleFormat();
  const { offerDiscountAPRDisplay } = useOfferDiscountAPRDisplay();

  const [inputValue, setInputValue] = useState<string>('');
  const [lastRateType, setLastRateType] = useState<'APR' | 'DISC'>(selectedRateType);
  const prevOpenRef = useRef<boolean | undefined>(false);
  const prevRateTypeRef = useRef<'APR' | 'DISC'>(selectedRateType);

  // reset rate input value when modal opens or rate type changes
  useEffect(() => {
    const modalJustOpened = open && !prevOpenRef.current;
    const rateTypeChanged = selectedRateType !== prevRateTypeRef.current;

    if (modalJustOpened || rateTypeChanged) {
      const currentRate =
        selectedRateType === 'APR' ? takerMarket.offerConfig.maxApr : takerMarket.offerConfig.maxDiscount;
      setInputValue(currentRate != null ? asNumber({ value: currentRate, decimals: 2 }) : '');

      if (rateTypeChanged) {
        setLastRateType(selectedRateType);
        prevRateTypeRef.current = selectedRateType;
      }
    }

    prevOpenRef.current = open;
  }, [open, selectedRateType, takerMarket.offerConfig.maxApr, takerMarket.offerConfig.maxDiscount, asNumber]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // Clear error when user starts typing
    if (onClearError) {
      onClearError();
    }

    // Parse and update selected strategy
    // Always call onSelect to keep state in sync with input, even when invalid
    const rate = parseRateInput(value);
    onSelect(rate ?? 0, selectedRateType);
  };

  const handleSliderChange = (value: number) => {
    const formattedValue = asNumber({ value, decimals: 2 });
    setInputValue(formattedValue);
    onSelect(value, selectedRateType);
  };

  // Calculate slider values from trending rate option
  // Returns null when no pricing data is available to hide the slider
  const sliderSuggested = useMemo(() => {
    if (!trendingRateOption) {
      return null;
    }

    return {
      rate: trendingRateOption.rate,
      lowSlider: trendingRateOption.suggestedRateStart ?? trendingRateOption.rate * 0.5,
      highSlider: trendingRateOption.suggestedRateEnd ?? trendingRateOption.rate * 1.5,
    };
  }, [trendingRateOption]);

  // Parse the input value once and reuse throughout the component
  const parsedRate = useMemo(() => parseRateInput(inputValue), [inputValue]);

  const sliderValue = useMemo(() => {
    if (!sliderSuggested) return null;

    // if rate type just changed, use trending rate of new rate type
    if (lastRateType !== selectedRateType) {
      return sliderSuggested.rate;
    }

    if (inputValue.trim() && parsedRate !== null) {
      return parsedRate;
    }

    return sliderSuggested.rate;
  }, [inputValue, parsedRate, sliderSuggested, lastRateType, selectedRateType]);

  // Determine if we should show error styling for the text input
  // Show input error only if there's an error message AND the user has selected this option
  const hasInputError = !!errorMessage && selected;

  return (
    <div>
      <RadioInput
        id="name-your-rate"
        className={cn('rounded-b-none border border-b-0', { 'border-red-600': hasNoSelectionError })}
        checked={selected}
        color="blue"
        name={radioGroupName}
        endSlot={<Chip label={t('core.dependsOnOffer')} size="sm" type="default" variant="outlined" />}
        label={
          <div className="flex-1">
            <div className="font-bold">
              {inputValue && parsedRate !== null ? (
                <span className="uppercase">{offerDiscountAPRDisplay(parsedRate, rateTypeLabel)}</span>
              ) : (
                t('core.setARate')
              )}
            </div>
            <div className="text-sm font-normal text-text-secondary">{t('core.nameYourRate')}</div>
          </div>
        }
        onChange={() => {
          onSelect(parsedRate ?? 0, selectedRateType);
        }}
        size="large"
        value="nameYourRate"
        variant="light"
      />
      <div
        className={cn('rounded-b-xl border border-t-0 border-stroke bg-white p-4 pt-0 transition-colors duration-200', {
          'bg-lightBlue-500': selected,
          'border-red-600': hasNoSelectionError,
        })}
      >
        <div className="rounded-xl border border-stroke bg-white py-4 pl-9 pr-4">
          <div className="space-y-1">
            <Label htmlFor="name-your-rate">{t('core.rate')}</Label>
            <div className="flex items-center gap-3">
              <TextInput
                value={inputValue}
                onChange={handleInputChange}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.key === ' ' && e.stopPropagation()}
                placeholder={t('core.setARate')}
                className="max-w-64 grow"
                endIcon={PercentIcon}
                hasError={hasInputError}
                id="name-your-rate"
              />
              <div className="font-medium uppercase">{rateTypeLabel}</div>
            </div>
            {hasInputError && <HelperText variant="error">{errorMessage}</HelperText>}
          </div>
          {sliderSuggested && sliderValue !== null && (
            <div className="max-w-72 grow pt-8">
              <NameYourRateSlider onChange={handleSliderChange} suggested={sliderSuggested} value={sliderValue} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Renders when the pricing service errors — falls back to NameYourRateOption without slider.
const PricingStrategyOptionsServiceError = ({
  takerMarket,
  selectedStrategy,
  onSelectStrategy,
  radioGroupName,
  rateTypeLabel,
  selectedRateType,
  open,
  errorMessage,
  hasNoSelectionError,
  onClearError,
}: Omit<PricingStrategyOptionsProps, 'frequency'> & { radioGroupName: string }) => {
  const { addError } = useDataDog();

  useEffect(() => {
    addError('Pricing Suggestions - Service Unavailable (Edit Rate Modal)', {
      marketUuid: takerMarket.marketUuid,
      supplierDivisionUuid: takerMarket.takerDivisionUuid,
    });
  }, [addError, takerMarket.marketUuid, takerMarket.takerDivisionUuid]);

  return (
    <NameYourRateOption
      takerMarket={takerMarket}
      selected={selectedStrategy?.type === 'nameYourRate'}
      onSelect={(rate, rateType) => onSelectStrategy({ type: 'nameYourRate', rate, rateType })}
      radioGroupName={radioGroupName}
      rateTypeLabel={rateTypeLabel}
      selectedRateType={selectedRateType}
      open={open}
      trendingRateOption={null}
      errorMessage={errorMessage}
      hasNoSelectionError={hasNoSelectionError}
      onClearError={onClearError}
    />
  );
};

// Isolated so usePricingSuggestion errors are caught by the wrapping ErrorBoundary rather
// than propagating to the entire modal. Contains all rate options including NameYourRateOption
// so the slider (which needs pricing data) renders normally when the service is healthy.
const PricingStrategyOptionsContent = ({
  selectedStrategy,
  onSelectStrategy,
  rateTypeLabel,
  selectedRateType,
  errorMessage = null,
  hasNoSelectionError = false,
  onClearError,
  takerMarket,
  frequency,
  open,
}: PricingStrategyOptionsProps) => {
  const radioGroupName = `pricing-strategy-${takerMarket.id}`;
  const { addError } = useDataDog();
  const hasLoggedEmptyRatesRef = useRef(false);
  const { data: pricingData } = usePricingSuggestion({
    takerMarkets: [takerMarket],
    isActionableOffer: true,
  });

  const options = useMemo(() => {
    if (!pricingData || !takerMarket) {
      return [];
    }

    return buildOptionsFromPricingData({
      pricingData,
      takerMarket,
      selectedRateType,
      frequency,
    }).filter((option) => option.type !== 'expressAccept');
  }, [pricingData, takerMarket, selectedRateType, frequency]);

  const trendingRateOption = useMemo(() => {
    return options.find((option) => option.type === 'trendingRate') ?? null;
  }, [options]);

  // Only flag when the market is eligible and has a matching pricing entry —
  // ineligible markets (eligibleInvoiceAmount <= 0) and missing supplierMarket
  // entries legitimately produce no options and are not anomalies.
  const hasEmptyRates = useMemo(() => {
    if (!pricingData || options.length > 0) {
      return false;
    }

    const isEligible = takerMarket.eligibleInvoiceAmount > 0;
    const hasMatchingEntry = pricingData.supplierMarkets.some(
      (tm) => tm.marketUuid === takerMarket.marketUuid && tm.supplierDivisionUuid === takerMarket.takerDivisionUuid
    );

    return isEligible && hasMatchingEntry;
  }, [
    pricingData,
    options.length,
    takerMarket.eligibleInvoiceAmount,
    takerMarket.marketUuid,
    takerMarket.takerDivisionUuid,
  ]);

  useEffect(() => {
    if (hasLoggedEmptyRatesRef.current || !hasEmptyRates) {
      return;
    }

    hasLoggedEmptyRatesRef.current = true;
    addError('Pricing Suggestions - Empty Rates', {
      marketUuid: takerMarket.marketUuid,
      supplierDivisionUuid: takerMarket.takerDivisionUuid,
    });
  }, [hasEmptyRates, takerMarket.marketUuid, takerMarket.takerDivisionUuid, addError]);

  return (
    <>
      {options.map((option) => (
        <SuggestedRateOption
          key={option.type}
          option={option}
          selected={
            selectedStrategy?.type === option.type &&
            selectedStrategy?.rate === option.rate &&
            selectedStrategy?.pricingRateUuid === option.pricingRateUuid
          }
          onSelect={() => onSelectStrategy({ ...option, type: option.type })}
          rateTypeLabel={rateTypeLabel}
          radioGroupName={radioGroupName}
          hasNoSelectionError={hasNoSelectionError}
        />
      ))}
      <NameYourRateOption
        takerMarket={takerMarket}
        selected={selectedStrategy?.type === 'nameYourRate'}
        onSelect={(rate, rateType) =>
          onSelectStrategy({
            type: 'nameYourRate',
            rate,
            rateType,
          })
        }
        radioGroupName={radioGroupName}
        rateTypeLabel={rateTypeLabel}
        selectedRateType={selectedRateType}
        open={open}
        trendingRateOption={trendingRateOption}
        errorMessage={errorMessage}
        hasNoSelectionError={hasNoSelectionError}
        onClearError={onClearError}
      />
    </>
  );
};

const PricingStrategyOptions = ({
  selectedStrategy,
  onSelectStrategy,
  rateTypeLabel,
  selectedRateType,
  errorMessage = null,
  hasNoSelectionError = false,
  onClearError,
  takerMarket,
  frequency,
  open,
}: PricingStrategyOptionsProps) => {
  const { t } = useTranslation();
  const radioGroupName = `pricing-strategy-${takerMarket.id}`;

  const serviceErrorFallback = (
    <PricingStrategyOptionsServiceError
      takerMarket={takerMarket}
      selectedStrategy={selectedStrategy}
      onSelectStrategy={onSelectStrategy}
      radioGroupName={radioGroupName}
      rateTypeLabel={rateTypeLabel}
      selectedRateType={selectedRateType}
      open={open}
      errorMessage={errorMessage}
      hasNoSelectionError={hasNoSelectionError}
      onClearError={onClearError}
    />
  );

  return (
    <fieldset>
      <legend className="sr-only">{t('core.pricingStrategy')}</legend>
      <div className="space-y-4">
        <ErrorBoundary fallback={serviceErrorFallback}>
          <PricingStrategyOptionsContent
            selectedStrategy={selectedStrategy}
            onSelectStrategy={onSelectStrategy}
            rateTypeLabel={rateTypeLabel}
            selectedRateType={selectedRateType}
            errorMessage={errorMessage}
            hasNoSelectionError={hasNoSelectionError}
            onClearError={onClearError}
            takerMarket={takerMarket}
            frequency={frequency}
            open={open}
          />
        </ErrorBoundary>
      </div>
    </fieldset>
  );
};

export default PricingStrategyOptions;
